import * as React from 'react';
import { type VariantProps, cva } from 'class-variance-authority';
import { cn } from 'cn';
import { Eye, EyeOff } from 'lucide-react';
import { useFormItemVariant } from './form-item-variant-context';
const inputVariants = cva([
// Theme tokens, not the light-only numeric scales: this input
// also renders inside pinned-dark surfaces (the wizard card),
// where a hardcoded near-black sits invisibly on a dark field.
// `refresh:` is the refreshed shell look, same as the web forms
// (FormInput): soft border, brand outline on focus, and matching
// success / error states.
'bg-background rounded-sm border border-input shadow-sm refresh:rounded-lg refresh:shadow-none', 'text-foreground w-full min-w-0 text-sm font-medium leading-5 refresh:font-normal', 'transition-[color,box-shadow,border,background] outline-none refresh:duration-150 refresh:ease-out', 'refresh:hover:border-ring', 'file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground', 'placeholder:text-muted-foreground/60 placeholder:font-normal', 'selection:bg-primary selection:text-primary-foreground', 'focus:border-ring focus:ring-ring focus:ring', 'refresh:focus:border-primary refresh:focus:ring-1 refresh:focus:ring-primary', 'data-[state=success]:border-primary data-[state=success]:ring-1 data-[state=success]:ring-primary', 'disabled:bg-tertiary-150 refresh:disabled:bg-muted disabled:pointer-events-none disabled:cursor-not-allowed', 'disabled:selection:bg-secondary-700', 'aria-invalid:border-red-500 aria-invalid:text-red-600 aria-invalid:ring-red-500', 'refresh:aria-invalid:border-destructive refresh:aria-invalid:text-foreground refresh:aria-invalid:ring-1 refresh:aria-invalid:ring-destructive'], {
  variants: {
    size: {
      sm: 'h-8 px-2 py-1.5',
      // The refresh side padding is added below, only when the caller
      // doesn't set its own (an icon inset like `pl-9` must win).
      md: 'h-9 p-2',
      lg: 'h-10 px-4 py-2.5'
    }
  },
  defaultVariants: {
    size: 'md'
  }
});
export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>, VariantProps<typeof inputVariants> {
  asChild?: boolean;
  ref?: React.Ref<HTMLInputElement>;
}
const Input = React.forwardRef<HTMLInputElement, InputProps>(({
  className,
  type,
  size,
  ...props
}, ref) => {
  const isInvalid = String(props['aria-invalid']).toLowerCase() === 'true';
  // Web-form field height (FormInput) inside a `field` FormItem.
  const isField = useFormItemVariant() === 'field' && !size;
  // A caller's own side padding (e.g. `pl-9` beside a search icon)
  // would otherwise lose to the refresh-variant padding, which sorts
  // later in the stylesheet, and slide the text under the icon.
  const hasOwnSidePadding = /(^|\s)(p|px|pl|pr|ps|pe)-/.test(className ?? '');
  return <input ref={ref} type={type} className={cn(inputVariants({
    size
  }), (size ?? 'md') === 'md' && !hasOwnSidePadding && 'refresh:px-3', isField && 'h-11', className,
  // Tailwind has inheritance issues when applying aria + seletion styles, so we override through cn
  {
    'selection:bg-red-100 selection:text-red-700': isInvalid
  })} {...props} />;
});
Input.displayName = 'Input';
export interface PasswordInputProps extends InputProps {
  ref?: React.Ref<HTMLInputElement>;
}
const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(({
  className,
  size = 'md',
  disabled,
  ...props
}, ref) => {
  const [showPassword, setShowPassword] = React.useState(false);
  const safeProps = {
    ...props
  };
  delete (safeProps as {
    type?: string;
  }).type;
  return <div className="relative">
        <Input ref={ref} type={showPassword ? 'text' : 'password'} size={size} className={cn(className, 'pr-10')} disabled={disabled} {...safeProps} />
        <button type="button" tabIndex={-1} disabled={disabled} onClick={() => setShowPassword(prev => !prev)} className="text-secondary-600 hover:text-secondary-900 absolute top-0 right-0 flex h-full items-center justify-center px-3 focus:outline-none disabled:pointer-events-none disabled:opacity-50" aria-label={showPassword ? 'Hide password' : 'Show password'}>
          {showPassword ? <EyeOff className="h-4 w-4 shrink-0" aria-hidden /> : <Eye className="h-4 w-4 shrink-0" aria-hidden />}
        </button>
      </div>;
});
PasswordInput.displayName = 'PasswordInput';
export { Input, PasswordInput };